import { QueryHookOptions, useQuery, useSubscription } from '@apollo/client';
import { InstanceRef } from '@decisiv/ui-components/lib/components/Popover';

import useTabVisibility from 'hooks/useTabVisibility';
import useFeatureFlag, { FEATURE_FLAGS } from 'utils/useFeatureFlag';
import { NOTIFICATION_REFRESH_INTERVAL } from 'components/TopNav/NotificationCenter/constants';
import { useEffect } from 'react';
import query from './unreadNotificationsCount.graphql';
import notificationsQuery from './notifications.graphql';
import notificationReceivedSubscription from './notificationReceived.graphql';

const REFETCH_QUERIES = [query, notificationsQuery];

export default function useUnreadNotificationsCount(
  notificationsModalRef: React.RefObject<InstanceRef>,
  options?: QueryHookOptions<
    UnreadNotificationsCountQuery,
    UnreadNotificationsCountQueryVariables
  >,
) {
  const isTabVisible = useTabVisibility();
  const notificationPollingDisabled = useFeatureFlag(
    FEATURE_FLAGS.DISABLE_NOTIFICATION_POLLING,
  );
  const websocketsEnabled = useFeatureFlag(FEATURE_FLAGS.ENABLE_WEBSOCKETS);
  const modalIsVisible = notificationsModalRef?.current?.isVisible;

  const enablePolling =
    isTabVisible &&
    !notificationPollingDisabled &&
    !modalIsVisible &&
    !websocketsEnabled;

  const { startPolling, stopPolling, ...result } = useQuery<
    UnreadNotificationsCountQuery,
    UnreadNotificationsCountQueryVariables
  >(query, options);

  useEffect(() => {
    if (enablePolling) {
      startPolling(NOTIFICATION_REFRESH_INTERVAL);
    } else {
      stopPolling();
    }
  }, [enablePolling, startPolling, stopPolling]);

  useSubscription<NotificationReceivedSubscription>(
    notificationReceivedSubscription,
    {
      skip: !websocketsEnabled,
      onData: ({ client }) => {
        client.refetchQueries({ include: REFETCH_QUERIES });
      },
    },
  );

  return result;
}
